import { ChainID, isSupportedChainId, roninSaigon } from '@shared/constants/chains';
import { useUserInfo } from '@shared/contexts/user-info';
import { useWallets } from '@shared/contexts/wallets-context';
import { useNavigateExternal } from '@shared/hooks/use-navigate-external';
import { NeonApi } from '@shared/services/neon-api';
import { WebshopApi } from '@shared/services/webshop-api';
import type { CountryInfoWithCurrency } from '@shared/types/country-info';
import { DashboardData } from '@shared/types/dashboard-data';
import {
  BaseOffer, CurrencyType, GuildOffer, Offer,
} from '@shared/types/offers';
import { getTokenAddressByChainId } from '@shared/utils/get-token-address-by-chain-id';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import { SuccessPopup } from 'components/successPopup/sucessPopup';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation as useTranslationHook } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getErc20AbiApproval } from 'utils/abi/getErc20AbiApproval';
import { Address, ContractFunctionExecutionError, erc20Abi } from 'viem';
import {
  bsc, bscTestnet, mainnet, ronin, sepolia,
} from 'viem/chains';
import { useReadContract } from 'wagmi';
import { icons } from '../../constants/assets';
import { RouterPathes } from '../../constants/router-paths';
import usePayWithPower from '../../hooks/usePayWithPower';
import { useTranslation } from '../../i18n/useTranslation';
import { handleSlippage } from '../../utils/handleSlippage';
import { GuildMember, GuildMemberSelection } from '../guildMemberSelection/guildMemberSelection';
import { Loader } from '../loader';
import { UserNameDetails } from '../UserNameDetails/UserNameDetails';
import { WalletChainButton } from '../walletChainButton/walletChainButton';
import { OfferDetails } from './offerDetails';
import { PurchaseButton } from './purchaseButton';

const CONTRACT_ADDRESESS: Record<ChainID, Address> = {
  [ronin.id]: import.meta.env.VITE_RONIN_CONTRACT_ADDRESS,
  [mainnet.id]: import.meta.env.VITE_ETHEREUM_CONTRACT_ADDRESS,
  [sepolia.id]: import.meta.env.VITE_ETHEREUM_CONTRACT_ADDRESS,
  [bsc.id]: import.meta.env.VITE_BNB_CONTRACT_ADDRESS,
  [bscTestnet.id]: import.meta.env.VITE_BNB_CONTRACT_ADDRESS,
  [roninSaigon.id]: import.meta.env.VITE_SAIGON_CONTRACT_ADDRESS,
};

const formatTokenBalance = (
  rawBalance: bigint | undefined,
  decimals: number | undefined,
) => {
  if (!rawBalance || !decimals) {
    return 0;
  }

  return Number(rawBalance) / 10 ** Number(decimals);
};

type OfferCheckoutParam = {
  offer: BaseOffer,
  userDetails: DashboardData
  currentOfferType: 'regular' | 'guild'
  guildMembers: GuildMember[]
  refetchGuildOffers: VoidFunction
  refetchOffers: VoidFunction
  currency: CurrencyType,
  selectedCountryInfo: CountryInfoWithCurrency,
  isGuildFounder: boolean,
}

export const OfferCheckout: React.FC<OfferCheckoutParam> = ({
  offer,
  userDetails,
  currentOfferType,
  guildMembers,
  refetchGuildOffers,
  refetchOffers,
  currency,
  selectedCountryInfo,
  isGuildFounder,
}) => {
  const {
    isWalletMatched, isConnected, connectedAddress, chainId,
  } = useWallets();
  const tokenAddress = !!chainId && isSupportedChainId(chainId)
    ? getTokenAddressByChainId(chainId as ChainID)
    : undefined;
  const { data: decimals, isLoading: isDecimalsLoading } = useReadContract({
    abi: erc20Abi,
    address: tokenAddress,
    functionName: 'decimals',
    query: { enabled: Boolean(connectedAddress && tokenAddress) },
  });
  const { data: powerTokenBalance, isLoading: isPowerTokenBalanceLoading } = useReadContract({
    abi: erc20Abi,
    address: tokenAddress,
    functionName: 'balanceOf',
    args: [connectedAddress as `0x${string}`],
    query: { enabled: Boolean(connectedAddress && tokenAddress) },
  });
  const formatedTokenBalance = formatTokenBalance(powerTokenBalance, decimals);
  const { payWithPower } = usePayWithPower();
  const [transactionPending, setTransactionPending] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedGuildMembers, setSelectedGuildMembers] = useState<string[]>([]);
  const { data: userInfo } = useUserInfo();
  const { i18n } = useTranslationHook();
  const { offerCheckoutTranslations, legalTranslations } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transactionSuccess, setTransactionSuccess] = useState(searchParams.get('success') === 'true');
  const [orderId, setOrderId] = useState(searchParams.get('orderId') ?? '');
  const isAccountLoading = isDecimalsLoading && isPowerTokenBalanceLoading;
  const navigateExternal = useNavigateExternal();

  const {
    shouldUpdatePrice,
  } = useMemo(() => handleSlippage(offer.slippageAllowed, offer.price), [offer.slippageAllowed, offer.price]);

  const isFiat = useMemo(() => currency === 'FIAT', [currency]);

  useEffect(() => {
    if (!shouldUpdatePrice) { return; }

    if (currentOfferType === 'guild') {
      refetchGuildOffers();
    } else {
      refetchOffers();
    }
  }, [shouldUpdatePrice, currentOfferType, refetchGuildOffers, refetchOffers]);

  const { mutateAsync: getNeon } = useMutation({
    mutationFn: NeonApi.getNeon,
    onSuccess: (data) => {
      navigateExternal(data.redirectUrl);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { decreaseDisabled, increaseDisabled } = useMemo(() => {
    const increasedAmount = quantity + 1;
    const decreasedAmount = quantity - 1;

    const individualSupply = currentOfferType === 'regular'
      ? (offer as Offer).individualSupply
      : (offer as GuildOffer).guildFounderSupply;

    return {
      decreaseDisabled: decreasedAmount < 1,
      increaseDisabled:
        increasedAmount > offer.globalSupply
        || increasedAmount > individualSupply,
    };
  }, [quantity, offer, currentOfferType]);

  const totalPrice = currentOfferType === 'guild' && selectedGuildMembers.length > 0
    ? quantity * offer.price * selectedGuildMembers.length
    : quantity * offer.price;

  const individualSupply = currentOfferType === 'regular'
    ? (offer as Offer).individualSupply
    : (offer as GuildOffer).guildFounderSupply;

  const isLimitReached = individualSupply === 0
    && (offer.maxGlobalSupply === -1 || offer.globalSupply > 0);
  const isSoldOut = offer.globalSupply === 0;

  const description = useMemo(() => {
    if (!offer) {
      return undefined;
    }

    const supply = currentOfferType === 'regular'
      ? (offer as Offer).individualSupply
      : (offer as GuildOffer).guildFounderSupply;

    const maxSupply = currentOfferType === 'regular'
      ? (offer as Offer).maxIndividualSupply
      : (offer as GuildOffer).maxFounderSupply;

    if (supply !== undefined) {
      const isUnlimited = maxSupply === null || maxSupply === undefined || maxSupply < 0;

      if (isUnlimited) {
        if (currentOfferType === 'guild') {
          return `${supply} ${offerCheckoutTranslations.rewardsPerGuildFounder}`;
        }
        return `${supply} ${offerCheckoutTranslations.rewardsPerPixionId}`;
      }

      if (maxSupply !== undefined) {
        if (supply === 0) {
          return `${maxSupply}/${maxSupply} ${offerCheckoutTranslations.maxClaim}`;
        }

        if (currentOfferType === 'guild') {
          return `${supply}/${maxSupply} ${offerCheckoutTranslations.rewardsPerGuildFounder}`;
        }

        return `${supply}/${maxSupply} ${offerCheckoutTranslations.rewardsPerPixionId}`;
      }
    }

    return undefined;
  }, [offer, currentOfferType]);

  const handleChangeQuantity = (newValue: number) => {
    if (newValue < 1) {
      setQuantity(1);

      return;
    }

    const currentIndividualSupply = currentOfferType === 'regular'
      ? (offer as Offer).individualSupply
      : (offer as GuildOffer).guildFounderSupply;

    if (newValue > offer.globalSupply || newValue > currentIndividualSupply) {
      return;
    }

    setQuantity(newValue);
  };

  const handlePurchase = async () => {
    try {
      setTransactionPending(true);

      if (isFiat) {
        const isGuildFiatCheckout = currentOfferType === 'guild';
        await getNeon({
          data: isGuildFiatCheckout
            ? {
              beneficiaries: selectedGuildMembers.length > 0
                ? selectedGuildMembers
                : [userInfo?.pixionId].filter(Boolean) as string[],
              chainId: chainId ?? 0,
              country: selectedCountryInfo.code.toUpperCase(),
              currency: selectedCountryInfo.currency,
              hash: offer.hash,
              lastSeenPrice: offer.price,
              locale: currentLang,
              offerId: offer.id,
              paymentType: 'fiat',
              quantity,
            }
            : {
              offerId: offer.id,
              quantity,
              offerType: 'fiat',
              currency: selectedCountryInfo.currency,
              country: selectedCountryInfo.code.toUpperCase(),
              locale: currentLang,
              hash: offer.hash,
            },
          checkoutPath: isGuildFiatCheckout ? '/guild/checkout' : '/webshop/checkout',
        });

        setTransactionPending(false);
        return;
      }

      if (!chainId) {
        throw new Error('chainId is required for purchase');
      }

      const data = currentOfferType === 'guild'
        ? await WebshopApi.checkoutGuildOffers({
          beneficiaries: selectedGuildMembers.length > 0
            ? selectedGuildMembers
            : [userInfo?.pixionId].filter(Boolean) as string[],
          country: selectedCountryInfo.code.toUpperCase(),
          currency: selectedCountryInfo.currency,
          lastSeenPrice: offer.price,
          locale: currentLang,
          offerId: offer.id,
          paymentType: 'power',
          quantity,
          chainId: chainId as ChainID,
          hash: offer.hash,
        })
        : await WebshopApi.checkoutRegularOffers({
          country: selectedCountryInfo.code.toUpperCase(),
          currency: selectedCountryInfo.currency,
          lastSeenPrice: offer.price,
          locale: currentLang,
          offerId: offer.id,
          offerType: 'power',
          quantity,
          userId: userDetails?.pixionId ?? '',
          chainId: chainId as ChainID,
          hash: offer.hash,
        });

      if (data?.order?.orderId) {
        setOrderId(data?.order?.orderId);
      }

      const contractAddress = data?.contractAddress;
      const envContractAddress = CONTRACT_ADDRESESS[chainId as ChainID];

      if (contractAddress.toLowerCase() !== envContractAddress.toLowerCase()) {
        throw new Error('Contract address mismatch');
      }

      const approved = await getErc20AbiApproval(data, connectedAddress, chainId as ChainID);

      if (!approved) {
        setTransactionPending(false);
        return;
      }

      const tx = await payWithPower({
        orderId: data?.order?.orderId,
        validUntil: data?.order?.validUntil,
        price: data?.order?.price,
        signature: data?.signature,
        contractAddress: envContractAddress,
      });

      setTransactionPending(false);
      if (tx) {
        setTransactionSuccess(true);
      }
    } catch (error) {
      setTransactionPending(false);

      if (error instanceof ContractFunctionExecutionError) {
        if (
          error.shortMessage.toLowerCase().includes('user rejected')
          || error.details.toLowerCase().includes('user rejected')
        ) {
          toast.error(offerCheckoutTranslations.userRejectedRequest);
          throw error;
        }

        toast.error(error.shortMessage);
        throw error;
      }

      if (error instanceof AxiosError) {
        const message = error.response?.data?.error?.message;

        // TODO: need to handle this better after testing available
        if (message === 'PRICE_DIFFERENCE_TOO_HIGH') {
          window.location.reload();
        }

        toast.error(message ?? offerCheckoutTranslations.purchaseFailed);
        throw error;
      }

      toast.error(offerCheckoutTranslations.purchaseFailed);
      throw error;
    }
  };

  if (transactionSuccess) {
    return (
      <SuccessPopup
        offer={offer as Offer}
        orderId={orderId}
        userDetails={userDetails}
        quantity={quantity}
        selectedMembersCount={currentOfferType === 'guild' && selectedGuildMembers.length > 0 ? selectedGuildMembers.length : undefined}
        currencyType={currency}
        currencyInfo={selectedCountryInfo}
        onClose={() => {
          navigate(RouterPathes.home);
          setTransactionSuccess(false);
        }}
      />
    );
  }

  return (
    <div className="relative w-full">
      <div
        // TODO move to plugin
        className={clsx(
          'bg-checkout-card-gradient relative z-10 mx-4 mt-6 flex flex-1 flex-col gap-10 rounded-xl border-[1.5px] border-solid border-black p-4 sm:px-10 sm:py-6 lg:flex-row',
          transactionPending && 'blur-sm brightness-50 grayscale-[0.2]',
        )}
      >
        <div className="flex flex-col items-center gap-6">
          <div className="w-full">
            <button
              type="button"
              className="bg-box_dark_blue flex size-10 items-center justify-center rounded-[6px]"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(RouterPathes.home))}
              aria-label="home"
            >
              <img src={icons.arrowLeftIcon} alt="arrow left" width={14} height={24} />
            </button>
          </div>
          <OfferDetails offer={offer as Offer} quantity={quantity} />
        </div>
        <div className="flex flex-1 flex-col-reverse gap-6 lg:flex-col">
          <div className="flex flex-col items-center justify-center gap-4 lg:flex-row lg:justify-start">
            {userDetails?.username && <UserNameDetails username={userDetails.username} />}
            <span className="objektiv-mk3-md text-lightBlue">
              {offerCheckoutTranslations.allPurchaseWillSendTo}
            </span>
          </div>
          <div className="bg-checkout-info-gradient flex flex-col gap-9 rounded-xl p-4 sm:px-8">
            <div className="flex flex-col gap-3">
              <h3 className="font-border poppins-2xl">
                {offer.title[currentLang]}
              </h3>
              <div className="flex justify-start">
                <div className="bg-box_dark_blue flex flex-col rounded-[8px] p-2 sm:px-6">
                  {isFiat ? (
                    <div className="font-border poppins-3xl">
                      {selectedCountryInfo.currencySymbol}
                      {' '}
                      {offer.price.toLocaleString()}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1">
                        <span className="font-border poppins-3xl">
                          {offer.price.toLocaleString()}
                        </span>
                        <img
                          className="size-[21px] md:size-[34px]"
                          src={icons.powerIcon}
                          alt="Power icon"
                        />
                      </div>
                      {offer.usdcPrice && (
                        <span className="objektiv-mk3-md text-lightBlue">
                          {offer.usdcPrice}
                          {' '}
                          {offerCheckoutTranslations.usdc}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <p className="font-objektiv-mk3 text-lightBlue text-[11.89px] font-bold md:text-[15px]">
                {offer.sku.description[currentLang]}
              </p>
            </div>
            <div className="bg-box_dark_blue flex flex-col rounded-[8px]">
              <div className="flex flex-col items-start justify-between gap-6 p-2 pb-6 sm:flex-row sm:px-6 sm:py-3 lg:flex-col xl:flex-row">
                <div className="flex flex-col gap-2">
                  <span className="font-border poppins-lg text-[#ECEAED]">
                    {offerCheckoutTranslations.quantity}
                  </span>
                  <div className="flex min-w-[180px] items-center justify-between rounded-[8px] bg-[#0E122C] px-4 py-2">
                    <button
                      type="button"
                      className="yellow-button font-border poppins-3xl size-[42px]"
                      disabled={decreaseDisabled}
                      onClick={() => handleChangeQuantity(quantity - 1)}
                    >
                      -
                    </button>
                    <span className="font-border poppins-3xl">{quantity}</span>
                    <button
                      type="button"
                      className="yellow-button font-border poppins-3xl size-[42px]"
                      disabled={increaseDisabled}
                      onClick={() => handleChangeQuantity(quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <span className="objektiv-mk3-md text-lightBlue">
                    {description}
                  </span>
                </div>
                {isGuildFounder
                  && currentOfferType === 'guild'
                  && (offer as GuildOffer).guildMemberSupplies
                  && (
                    <div className="1xl:block w-full sm:hidden lg:block xl:hidden">
                      <GuildMemberSelection
                        offer={offer as GuildOffer}
                        members={guildMembers}
                        selectedMembers={selectedGuildMembers}
                        onSelectionChange={setSelectedGuildMembers}
                        selectedQuantity={quantity}
                      />
                    </div>
                  )}
                <div className="flex flex-col items-start gap-2 sm:items-end lg:items-start xl:items-end">
                  <div className="flex flex-col items-center lg:flex-row lg:gap-2">
                    <span className="font-border poppins-lg text-[#ECEAED]">
                      {offerCheckoutTranslations.total}
                    </span>
                    {!isFiat && isConnected && connectedAddress && (
                      <WalletChainButton type="checkout" />
                    )}
                  </div>
                  <div className="flex w-full flex-col items-end rounded-[8px] bg-[#0E122C] px-4 py-2">
                    {isFiat ? (
                      <div className="font-border poppins-3xl">
                        {selectedCountryInfo.currencySymbol}
                        {' '}
                        {(quantity * offer.price).toLocaleString()}
                      </div>
                    ) : (
                      <>
                        <div className="flex w-full flex-row items-center">
                          <span className="font-border poppins-3xl">
                            {totalPrice.toLocaleString()}
                          </span>
                          <img
                            className="1xl:mr-7 ml-2 size-[21px] md:size-[34px]"
                            src={icons.powerIcon}
                            alt="Power icon"
                          />
                        </div>
                        {offer.usdcPrice && (
                          <span className="objektiv-mk3-md text-lightBlue">
                            {(currentOfferType === 'guild' && selectedGuildMembers.length > 0
                              ? quantity * offer.usdcPrice * selectedGuildMembers.length
                              : quantity * offer.usdcPrice
                            ).toFixed(2)}
                            {' '}
                            {offerCheckoutTranslations.usdc}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {!isFiat && (
                    !isDecimalsLoading || !isPowerTokenBalanceLoading
                      ? (
                        <span className="objektiv-mk3-md text-lightBlue">
                          {offerCheckoutTranslations.balance}
                          :
                          {' '}
                          {formatedTokenBalance.toLocaleString()}
                          {' '}
                          {offerCheckoutTranslations.power}
                        </span>
                      )
                      : (
                        <Loader
                          width={18}
                          height={18}
                        />
                      )
                  )}
                </div>
              </div>
              {isGuildFounder
                && currentOfferType === 'guild'
                && (offer as GuildOffer).guildMemberSupplies
                && (
                  <div className="1xl:hidden mb-4 hidden w-full sm:block lg:hidden xl:block">
                    <GuildMemberSelection
                      offer={offer as GuildOffer}
                      members={guildMembers}
                      selectedMembers={selectedGuildMembers}
                      onSelectionChange={setSelectedGuildMembers}
                      selectedQuantity={quantity}
                    />
                  </div>
                )}
              <PurchaseButton
                isWalletMatched={isWalletMatched}
                isWalletConnected={isConnected}
                transactionPending={transactionPending}
                onPurchase={handlePurchase}
                isInsufficientBalance={totalPrice > formatedTokenBalance}
                isLimitReached={isLimitReached}
                isSoldOut={isSoldOut}
                offer={offer as GuildOffer}
                selectedQuantity={quantity}
                selectedMembers={selectedGuildMembers}
                isAccountLoading={isAccountLoading}
                currency={currency}
              />
            </div>
            <span className="objektiv-mk3-md text-lightBlue text-center">
              {offerCheckoutTranslations.confirmingPolicy}
              {' '}
              <Link
                className="text-[#f1cf2d] underline"
                to="https://fableborne.com/terms"
                target="_blank"
              >
                {legalTranslations.termsShort}
              </Link>
              ,
              {' '}
              <Link
                className="text-[#f1cf2d] underline"
                to="https://fableborne.com/eula"
                target="_blank"
              >
                {legalTranslations.eula}
              </Link>
              {' '}
              {offerCheckoutTranslations.and}
              {' '}
              <Link
                className="text-[#f1cf2d] underline"
                to="https://fableborne.com/privacy"
                target="_blank"
              >
                {legalTranslations.privacyPolicy}
              </Link>
            </span>
          </div>
        </div>
      </div>
      {transactionPending && (
        <div className="font-border poppins-2xl absolute inset-0 z-[1000] flex items-center justify-center">
          {offerCheckoutTranslations.transactionInProgress}
        </div>
      )}
    </div>
  );
};
